import React, { useCallback, useEffect, useState } from "react";
import { api, formatDateBR, todayISO } from "@/lib/api";
import { CHECKLIST_TEMPLATE, flattenChecklist } from "@/lib/checklistTemplate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Printer, ClipboardCheck, AlertTriangle, CheckCircle2, MinusCircle } from "lucide-react";

const STATUS_ICON = {
    ok: { icon: CheckCircle2, color: "text-green-600", label: "OK", cls: "bg-green-100 text-green-800 border-green-300" },
    nao_ok: { icon: AlertTriangle, color: "text-red-600", label: "Não OK", cls: "bg-red-100 text-red-800 border-red-300" },
    na: { icon: MinusCircle, color: "text-gray-400", label: "N/A", cls: "bg-gray-100 text-gray-600 border-gray-300" },
};

const emptyInspection = () => ({
    veiculo_modelo: "", veiculo_placa: "",
    motorista_nome: "", cpf: "",
    data: todayISO(), hora: new Date().toTimeString().slice(0, 5),
    km_atual: null, combustivel_nivel: "",
    tipo_viagem: "saida",
    itens: flattenChecklist(),
    observacoes_gerais: "",
});

export default function Inspections() {
    const [list, setList] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyInspection());
    const [editingId, setEditingId] = useState(null);
    const [mes, setMes] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (mes) params.mes = mes;
        const [r, d, v] = await Promise.all([
            api.get("/inspections", { params }),
            api.get("/drivers"),
            api.get("/vehicles"),
        ]);
        setList(r.data);
        setDrivers(d.data);
        setVehicles(v.data);
        setLoading(false);
    }, [mes]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm(emptyInspection()); setEditingId(null); setOpen(true); };
    const openEdit = (r) => {
        setForm({
            veiculo_modelo: r.veiculo_modelo || "", veiculo_placa: r.veiculo_placa,
            motorista_nome: r.motorista_nome, cpf: r.cpf || "",
            data: r.data, hora: r.hora || "",
            km_atual: r.km_atual ?? null, combustivel_nivel: r.combustivel_nivel || "",
            tipo_viagem: r.tipo_viagem || "saida",
            itens: r.itens && r.itens.length ? r.itens : flattenChecklist(),
            observacoes_gerais: r.observacoes_gerais || "",
        });
        setEditingId(r.id);
        setOpen(true);
    };

    const selectDriver = (id) => {
        const d = drivers.find((x) => x.id === id);
        if (d) setForm((f) => ({ ...f, motorista_nome: d.nome, cpf: d.cpf }));
    };
    const selectVehicle = (id) => {
        const v = vehicles.find((x) => x.id === id);
        if (v) setForm((f) => ({ ...f, veiculo_placa: v.placa, veiculo_modelo: v.modelo }));
    };

    const setItemStatus = (idx, status) => {
        setForm((f) => {
            const itens = [...f.itens];
            itens[idx] = { ...itens[idx], status };
            return { ...f, itens };
        });
    };
    const setItemObs = (idx, obs) => {
        setForm((f) => {
            const itens = [...f.itens];
            itens[idx] = { ...itens[idx], observacao: obs };
            return { ...f, itens };
        });
    };

    const save = async () => {
        if (!form.veiculo_placa.trim() || !form.motorista_nome.trim() || !form.data) {
            toast.error("Preencha placa, motorista e data");
            return;
        }
        const payload = { ...form, km_atual: form.km_atual === null || form.km_atual === "" ? null : parseFloat(form.km_atual) };
        try {
            if (editingId) {
                await api.put(`/inspections/${editingId}`, payload);
                toast.success("Inspeção atualizada!");
            } else {
                await api.post("/inspections", payload);
                toast.success("Inspeção registrada!");
            }
            setOpen(false);
            load();
        } catch {
            toast.error("Erro ao salvar");
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/inspections/${id}`);
            toast.success("Inspeção removida");
            load();
        } catch {
            toast.error("Erro ao remover");
        } finally {
            setConfirmDelete(null);
        }
    };

    // Group items by categoria for display
    const itemsByCategoria = {};
    form.itens.forEach((item, idx) => {
        const cat = item.categoria || "Outros";
        if (!itemsByCategoria[cat]) itemsByCategoria[cat] = [];
        itemsByCategoria[cat].push({ ...item, idx });
    });

    return (
        <div className="space-y-6" data-testid="inspections-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-3xl font-extrabold text-foreground flex items-center gap-3">
                        <ClipboardCheck className="w-7 h-7 text-primary" /> Checklist de Veículos
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">Inspeção visual antes/depois de cada viagem</p>
                </div>
                <Button onClick={openCreate} data-testid="btn-new-inspection" className="gap-2">
                    <Plus className="w-4 h-4" /> Nova inspeção
                </Button>
            </div>

            <Card>
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="w-48" data-testid="input-month-inspection" />
                        {mes && <Button variant="ghost" size="sm" onClick={() => setMes("")}>Limpar mês</Button>}
                        <div className="ml-auto text-sm text-muted-foreground">
                            {list.length} inspeções
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-muted-foreground py-8 text-center">Carregando...</div>
                    ) : list.length === 0 ? (
                        <div className="py-14 text-center border-2 border-dashed border-border rounded-xl">
                            <ClipboardCheck className="w-10 h-10 mx-auto text-muted-foreground" />
                            <div className="mt-3 font-semibold">Nenhuma inspeção registrada</div>
                        </div>
                    ) : (
                        <div className="space-y-2" data-testid="inspections-list">
                            {list.map((r) => {
                                const naoOk = (r.itens || []).filter((i) => i.status === "nao_ok").length;
                                return (
                                    <div key={r.id} className="border border-border rounded-lg p-4 flex items-start justify-between gap-3 bg-white hover:shadow-sm">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <div className="font-bold text-foreground">{r.veiculo_modelo || "Veículo"} — <span className="font-mono">{r.veiculo_placa}</span></div>
                                                <Badge variant={naoOk > 0 ? "destructive" : "default"} className="uppercase text-[10px]">
                                                    {naoOk > 0 ? `${naoOk} não OK` : "Tudo OK"}
                                                </Badge>
                                                <Badge variant="secondary" className="text-[10px] uppercase">{r.tipo_viagem === "chegada" ? "Chegada" : "Saída"}</Badge>
                                            </div>
                                            <div className="mt-1 text-sm text-muted-foreground">
                                                {r.motorista_nome} · {formatDateBR(r.data)} {r.hora ? `às ${r.hora}` : ""} {r.km_atual != null ? `· ${r.km_atual.toLocaleString("pt-BR")} km` : ""}
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button variant="outline" size="sm" onClick={() => window.open(`/inspecao/${r.id}/imprimir`, "_blank")} data-testid={`btn-print-insp-${r.id}`}>
                                                <Printer className="w-4 h-4" />
                                            </Button>
                                            <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`btn-edit-insp-${r.id}`}>
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(r)} data-testid={`btn-delete-insp-${r.id}`}>
                                                <Trash2 className="w-4 h-4 text-destructive" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-4xl" data-testid="inspection-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Editar inspeção" : "Nova inspeção de veículo"}</DialogTitle>
                        <DialogDescription>Preencha os dados e marque cada item do checklist</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-2">
                        <div className="text-xs font-semibold uppercase tracking-wider text-primary border-b border-border pb-1">Dados gerais</div>
                        <div>
                            <Label>Veículo (opcional)</Label>
                            <Select onValueChange={selectVehicle}>
                                <SelectTrigger data-testid="select-insp-vehicle"><SelectValue placeholder="Preencher automaticamente..." /></SelectTrigger>
                                <SelectContent>
                                    {vehicles.length === 0 && <div className="p-3 text-xs text-muted-foreground">Nenhum veículo cadastrado</div>}
                                    {vehicles.map((v) => (<SelectItem key={v.id} value={v.id}>{v.modelo} — {v.placa}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Modelo do veículo</Label>
                                <Input value={form.veiculo_modelo} onChange={(e) => setForm({ ...form, veiculo_modelo: e.target.value })} data-testid="input-insp-modelo" />
                            </div>
                            <div>
                                <Label>Placa *</Label>
                                <Input value={form.veiculo_placa} onChange={(e) => setForm({ ...form, veiculo_placa: e.target.value.toUpperCase() })} data-testid="input-insp-placa" />
                            </div>
                        </div>
                        <div>
                            <Label>Motorista (opcional - selecione cadastrado)</Label>
                            <Select onValueChange={selectDriver}>
                                <SelectTrigger data-testid="select-insp-driver"><SelectValue placeholder="Preencher automaticamente..." /></SelectTrigger>
                                <SelectContent>
                                    {drivers.length === 0 && <div className="p-3 text-xs text-muted-foreground">Nenhum motorista cadastrado</div>}
                                    {drivers.map((d) => (<SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Nome do motorista *</Label>
                                <Input value={form.motorista_nome} onChange={(e) => setForm({ ...form, motorista_nome: e.target.value })} data-testid="input-insp-motorista" />
                            </div>
                            <div>
                                <Label>CPF</Label>
                                <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} data-testid="input-insp-cpf" />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label>Data *</Label>
                                <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="input-insp-data" />
                            </div>
                            <div>
                                <Label>Hora</Label>
                                <Input type="time" value={form.hora} onChange={(e) => setForm({ ...form, hora: e.target.value })} data-testid="input-insp-hora" />
                            </div>
                            <div>
                                <Label>Tipo</Label>
                                <Select value={form.tipo_viagem} onValueChange={(v) => setForm({ ...form, tipo_viagem: v })}>
                                    <SelectTrigger data-testid="select-insp-tipo"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="saida">Saída</SelectItem>
                                        <SelectItem value="chegada">Chegada</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>KM atual</Label>
                                <Input type="number" step="0.1" value={form.km_atual ?? ""} onChange={(e) => setForm({ ...form, km_atual: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="input-insp-km" />
                            </div>
                            <div>
                                <Label>Nível de combustível</Label>
                                <Select value={form.combustivel_nivel || "none"} onValueChange={(v) => setForm({ ...form, combustivel_nivel: v === "none" ? "" : v })}>
                                    <SelectTrigger data-testid="select-insp-comb"><SelectValue placeholder="Selecione" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">—</SelectItem>
                                        <SelectItem value="reserva">Reserva</SelectItem>
                                        <SelectItem value="1/4">1/4</SelectItem>
                                        <SelectItem value="1/2">1/2</SelectItem>
                                        <SelectItem value="3/4">3/4</SelectItem>
                                        <SelectItem value="cheio">Cheio</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="text-xs font-semibold uppercase tracking-wider text-primary border-b border-border pb-1 pt-4">Inspeção Visual</div>

                        {Object.entries(itemsByCategoria).map(([categoria, items]) => (
                            <div key={categoria} className="border border-border rounded-lg p-3 bg-secondary/30">
                                <div className="font-bold text-sm text-primary mb-2">{categoria}</div>
                                <div className="space-y-2">
                                    {items.map((item) => (
                                        <ChecklistRow key={item.idx} item={item} onStatus={(s) => setItemStatus(item.idx, s)} onObs={(o) => setItemObs(item.idx, o)} />
                                    ))}
                                </div>
                            </div>
                        ))}

                        <div>
                            <Label>Observações gerais</Label>
                            <Textarea value={form.observacoes_gerais} onChange={(e) => setForm({ ...form, observacoes_gerais: e.target.value })} data-testid="input-insp-obs" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        {editingId && (
                            <Button variant="secondary" className="gap-2" onClick={() => window.open(`/inspecao/${editingId}/imprimir`, "_blank")}>
                                <Printer className="w-4 h-4" /> Imprimir
                            </Button>
                        )}
                        <Button onClick={save} data-testid="btn-save-inspection">Salvar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover inspeção?</AlertDialogTitle>
                        <AlertDialogDescription>A inspeção do veículo <strong>{confirmDelete?.veiculo_placa}</strong> será removida.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(confirmDelete.id)} data-testid="btn-confirm-delete-insp">Remover</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function ChecklistRow({ item, onStatus, onObs }) {
    return (
        <div className="bg-white rounded border border-border p-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm flex-1 min-w-[200px]">{item.label}</div>
                <div className="flex gap-1">
                    {Object.entries(STATUS_ICON).map(([key, info]) => {
                        const Icon = info.icon;
                        const active = item.status === key;
                        return (
                            <Button
                                key={key}
                                type="button"
                                variant={active ? "default" : "outline"}
                                size="sm"
                                className={`gap-1 h-8 text-xs ${active && key === "ok" ? "bg-green-600 hover:bg-green-700" : ""} ${active && key === "nao_ok" ? "bg-red-600 hover:bg-red-700" : ""}`}
                                onClick={() => onStatus(key)}
                            >
                                <Icon className="w-3 h-3" /> {info.label}
                            </Button>
                        );
                    })}
                </div>
            </div>
            {item.status === "nao_ok" && (
                <Input
                    placeholder="Descreva o problema..."
                    value={item.observacao}
                    onChange={(e) => onObs(e.target.value)}
                    className="mt-2 text-xs"
                />
            )}
        </div>
    );
}
